import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Text,
  HStack,
  VStack,
  Link,
  Flex,
  UnorderedList,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  Textarea,
  Button,
  Checkbox,
  useDisclosure,
  useToast,
  Spinner,
  Portal,
} from '@chakra-ui/react';
import {
  ExternalLink,
  Plus,
  Search,
  Pencil,
  Trash2,
  GripVertical,
  Sparkles,
  AlertTriangle,
  Mail,
  Phone,
  User,
  Building2,
  Users,
  DollarSign,
  Layers,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { formatCurrency, formatEmployees } from '../../utils/tableUtils';
import { mergeDetailViewFields } from '../../utils/detailViewCustomFields';
import DetailViewFieldsList from './DetailViewFieldsList';
import api from '../../services/api';

// Custom LinkedIn Icon
const LinkedInIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// 6-dot grip handle component
const DragHandle = ({ orientation = 'vertical', ...props }) => (
  <Flex
    align="center"
    justify="center"
    cursor="grab"
    color="whiteAlpha.300"
    _hover={{ color: 'whiteAlpha.600' }}
    transition="color 0.12s ease"
    flexShrink={0}
    {...props}
  >
    <GripVertical size={14} strokeWidth={1.5} style={orientation === 'horizontal' ? { transform: 'rotate(90deg)' } : undefined} />
  </Flex>
);

const _URL_RE = /^https?:\/\/\S+$/i;
const _MD_LINK_RE = /\[.*?\]\(.*?\)/;

const _renderInlineVal = (val) => {
  if (val === null || val === undefined) return '—';
  const str = String(val);
  if (_URL_RE.test(str.trim())) {
    return (
      <Link
        href={str.trim()}
        isExternal
        color="blue.400"
        _hover={{ opacity: 0.8 }}
        onClick={(e) => e.stopPropagation()}
      >
        {str.trim().replace(/^https?:\/\/(www\.)?/, '')}
      </Link>
    );
  }
  if (_MD_LINK_RE.test(str)) {
    return (
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => <Text as="span" {...props} />,
          a: ({ node, ...props }) => (
            <Link
              {...props}
              color="blue.400"
              textDecoration="none"
              _hover={{ textDecoration: 'underline', color: 'blue.300' }}
              isExternal
              onClick={(e) => e.stopPropagation()}
            />
          ),
        }}
      >
        {str}
      </ReactMarkdown>
    );
  }
  return str;
};

const renderFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return (
      <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7" fontWeight="400">—</Text>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7">—</Text>;
    }
    return (
      <UnorderedList spacing={1} ml={5} mt={1}>
        {value.map((item, idx) => (
          <ListItem key={idx} fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" fontWeight="400">
            {_renderInlineVal(item)}
          </ListItem>
        ))}
      </UnorderedList>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7">—</Text>;
    }
    return (
      <VStack align="start" spacing={1} mt={1}>
        {entries.map(([key, val]) => (
          <Text key={key} fontSize="sm" lineHeight="1.7">
            <Text as="span" fontWeight="600" color="white">{key}:</Text>{' '}
            <Text as="span" color="whiteAlpha.800">{_renderInlineVal(val)}</Text>
          </Text>
        ))}
      </VStack>
    );
  }

  const str = String(value);
  const trimmed = str.trim();
  if (!trimmed) {
    return <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7">—</Text>;
  }
  if (_URL_RE.test(trimmed)) {
    return (
      <Link
        href={trimmed}
        isExternal
        fontSize="sm"
        color="blue.400"
        fontWeight="400"
        lineHeight="1.7"
        _hover={{ opacity: 0.8 }}
        onClick={(e) => e.stopPropagation()}
      >
        {trimmed.replace(/^https?:\/\/(www\.)?/, '')}
      </Link>
    );
  }
  if (_MD_LINK_RE.test(str)) {
    return (
      <Box
        fontSize="sm"
        color="whiteAlpha.800"
        lineHeight="1.7"
        fontWeight="400"
        sx={{ '& p': { margin: 0, whiteSpace: 'pre-wrap' }, '& p + p': { marginTop: '0.75em' } }}
      >
        <ReactMarkdown
          components={{
            p: ({ node, ...props }) => (
              <Text as="p" fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" fontWeight="400" mb={0} whiteSpace="pre-wrap" {...props} />
            ),
            a: ({ node, ...props }) => (
              <Link
                {...props}
                color="blue.400"
                textDecoration="none"
                _hover={{ textDecoration: 'underline', color: 'blue.300' }}
                isExternal
                onClick={(e) => e.stopPropagation()}
              />
            ),
          }}
        >
          {str}
        </ReactMarkdown>
      </Box>
    );
  }
  return (
    <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" whiteSpace="pre-wrap" fontWeight="400">
      {str}
    </Text>
  );
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  if (cleaned.length === 11 && cleaned[0] === '1') return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  return phoneNumber;
};

/**
 * Notion-style contact detail view
 * Replaces the table when a contact row is clicked
 */
const ContactDetailView = ({
  contact,
  onBack,
  customFields,            // contactFields.fieldDefinitions from VirtualizedPersonaResults
  onFieldsChange,
  campaignId,
  onContactedChange,       // (membership_id, field_id, value) => void
  onNotesChange,           // (membership_id, field_id, value) => void
  contactedFieldId,
  notesFieldId,
  companiesData = [],      // full company objects from VirtualizedPersonaResults
  onPrev,
  onNext,
  currentIndex,
  totalCount,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [fieldName, setFieldName] = useState('');
  const [fieldDescription, setFieldDescription] = useState('');
  const [modalMode, setModalMode] = useState('quick');
  const [customPrompt, setCustomPrompt] = useState('');
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Notes editing state
  const [notesValue, setNotesValue] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const notesDebounceRef = useRef(null);

  // Company section collapse state
  const [companyExpanded, setCompanyExpanded] = useState(true);

  // Resolve parent company: prefer the snapshot embedded by the API, fall back to the
  // lazy-loaded companiesData for campaigns already visited on the Companies tab.
  const parentCompany = React.useMemo(() => {
    if (contact?.parent_company) return contact.parent_company;
    if (!contact?.company_uuid || !companiesData?.length) return null;
    return companiesData.find(c => c.elasticsearch_id === contact.company_uuid) || null;
  }, [contact?.parent_company, contact?.company_uuid, companiesData]);

  const [detailFields, setDetailFields] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);

  const isUrl = (str) => {
    try {
      const url = new URL(str);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return 'N/A';
      return value.map(item =>
        item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item)
      ).join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    const trimmed = value.trim();
    if (trimmed === 'True' || trimmed === 'true') return 'true';
    if (trimmed === 'False' || trimmed === 'false') return 'false';
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'boolean') return String(parsed);
      if (typeof parsed === 'number') return String(parsed);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return 'N/A';
        return parsed.map(item =>
          item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item)
        ).join(', ');
      }
      if (parsed !== null && typeof parsed === 'object') return JSON.stringify(parsed, null, 2);
    } catch {}
    return trimmed;
  };

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [renamingFieldId, setRenamingFieldId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const contextMenuRef = useRef(null);
  const renameInputRef = useRef(null);

  // Drag state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Initialise notes from custom_fields
  useEffect(() => {
    if (contact && notesFieldId) {
      setNotesValue(contact.custom_fields?.[notesFieldId] || '');
    }
  }, [contact, notesFieldId]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  useEffect(() => {
    if (renamingFieldId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingFieldId]);

  useEffect(() => {
    return () => {
      if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    };
  }, []);

  // Research DB fields + all other campaign custom fields with values on this contact.
  useEffect(() => {
    if (!campaignId || !contact) return;
    const membershipId = contact.membership_id;
    if (!membershipId) return;

    let cancelled = false;
    setFieldsLoaded(false);

    api.get(`/api/research/campaigns/${campaignId}/contacts/${membershipId}/fields`)
      .then(({ data }) => {
        if (cancelled) return;
        const merged = mergeDetailViewFields({
          apiFields: data.fields || [],
          fieldDefinitions: customFields,
          entityCustomFields: contact.custom_fields,
          excludeFieldIds: [notesFieldId, contactedFieldId],
        });
        setDetailFields(merged);
        setFieldsLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load saved contact fields:', err);
        setFieldsLoaded(true);
      });

    return () => { cancelled = true; };
  }, [campaignId, contact, customFields, notesFieldId, contactedFieldId]);

  // Report field changes to parent
  useEffect(() => {
    if (onFieldsChange && fieldsLoaded) {
      const allFields = detailFields.map(f => ({
        id: f.id,
        label: f.label,
        description: f.description,
        config_id: f.config_id || null,
      }));
      onFieldsChange(allFields);
    }
  }, [detailFields, onFieldsChange, fieldsLoaded]);

  if (!contact) return null;

  const contactedValue = contactedFieldId ? Boolean(contact.custom_fields?.[contactedFieldId]) : false;

  const handleDeleteField = async (fieldId) => {
    setContextMenu(null);
    const field = detailFields.find(f => f.id === fieldId);
    const label = field?.label || 'this field';
    try {
      const { data } = await api.get(`/api/research/fields/${fieldId}/value-count`);
      const count = data.value_count || 0;
      if (count > 1) {
        setDeleteConfirm({ fieldId, label, valueCount: count });
        return;
      }
      await executeDelete(fieldId);
    } catch {
      await executeDelete(fieldId);
    }
  };

  const executeDelete = async (fieldId) => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/research/fields/${fieldId}`);
      setDetailFields(prev => prev.filter(f => f.id !== fieldId));
      setDeleteConfirm(null);
    } catch (err) {
      toast({
        title: 'Failed to delete field',
        description: err?.response?.data?.detail || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartRename = (field) => {
    setRenamingFieldId(field.id);
    setRenameValue(field.label);
    setContextMenu(null);
  };

  const handleFinishRename = async (fieldId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenamingFieldId(null); setRenameValue(''); return; }
    setDetailFields(prev =>
      prev.map(f => f.id === fieldId ? { ...f, label: trimmed } : f)
    );
    setRenamingFieldId(null);
    setRenameValue('');
    try {
      await api.patch(`/api/research/fields/${fieldId}/rename`, { display_name: trimmed });
    } catch (err) {
      toast({ title: 'Failed to rename field', description: err?.response?.data?.detail || 'Something went wrong', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      setDetailFields(prev => {
        const arr = [...prev];
        const [moved] = arr.splice(dragIdx, 1);
        arr.splice(dragOverIdx, 0, moved);
        return arr;
      });
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleAddField = async () => {
    if (!fieldName.trim()) return;

    const label = fieldName.trim();
    const description = fieldDescription.trim();
    const fieldId = `dynamic_${Date.now()}`;

    const newField = { id: fieldId, label, description, value: null, loading: true };
    setDetailFields(prev => [...prev, newField]);

    setFieldName('');
    setFieldDescription('');
    onClose();

    try {
      const { data } = await api.post(
        `/api/research/campaigns/${campaignId}/deep-dive`,
        {
          prompt: `${label}: ${description}`,
          entity_type: 'contact',
          entity_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
          membership_id: contact.membership_id || '',
          max_fields: 1,
          display_name_override: label,
        },
      );
      const field = data.fields?.[0];
      if (!field) return;

      const realId = field.id || fieldId;
      const configId = data.config_id;

      setDetailFields(prev =>
        prev.map(f =>
          f.id === fieldId
            ? {
                ...f,
                id: realId,
                config_id: configId,
                value: field.value ?? 'No data found',
                loading: false,
              }
            : f
        )
      );
    } catch (err) {
      setDetailFields(prev =>
        prev.map(f => f.id === fieldId ? { ...f, value: 'Research failed — try again', loading: false } : f)
      );
    }
  };

  const handleDeepDive = async () => {
    if (!customPrompt.trim()) return;
    const prompt = customPrompt.trim();
    setDeepDiveLoading(true);
    try {
      const { data } = await api.post(
        `/api/research/campaigns/${campaignId}/deep-dive`,
        {
          prompt,
          entity_type: 'contact',
          entity_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
          membership_id: contact.membership_id || '',
        },
      );

      // Response contains resolved values — populate fields immediately
      const returnedFields = (data.fields || []).map(f => ({
        id: f.id,
        label: f.label,
        description: f.description || '',
        value: f.value ?? '',
        loading: false,
        config_id: data.config_id || null,
      }));
      const incomingIds = new Set(returnedFields.map(f => f.id));

      setDetailFields(prev => [
        ...prev.filter(f => !incomingIds.has(f.id)),
        ...returnedFields,
      ]);

      setCustomPrompt('');
      onClose();
    } catch (err) {
      console.error('Deep dive failed:', err);
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const openAddFieldModal = () => {
    onOpen();
  };

  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact';
  const phones = contact.phone_number ? contact.phone_number.split(/,\s*/).filter(p => p.trim()) : [];
  const phoneTypes = contact.phone_type ? contact.phone_type.split(/,\s*/).map(t => t.trim()) : [];

  return (
    <Box
      h="100%"
      position="relative"
      bg="#0a0a0a"
    >
      <Box
        h="100%"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.08)', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.15)' },
        }}
      >
      <Box maxW="820px" mx="auto" px={8} py={6}>
        {/* Counter */}
        {totalCount > 0 && (
          <Flex justify="flex-end" mb={4}>
            <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">
              {currentIndex + 1} / {totalCount}
            </Text>
          </Flex>
        )}


        {/* Contact name heading */}
        <HStack spacing={3} mb={4} align="center">
          <Flex w="36px" h="36px" borderRadius="8px" bg="whiteAlpha.100" align="center" justify="center" flexShrink={0}>
            <User size={18} strokeWidth={1.5} color="rgba(255,255,255,0.5)" />
          </Flex>
          <VStack align="start" spacing={0.5}>
            <Text fontSize="2xl" fontWeight="600" color="white" letterSpacing="-0.02em" lineHeight="1.2">
              {fullName}
            </Text>
            {contact.position && (
              <Text fontSize="md" color="whiteAlpha.700" fontWeight="400" lineHeight="1.3">{contact.position}</Text>
            )}
          </VStack>
        </HStack>

        {/* ───── Horizontal metadata row ───── */}
        <HStack spacing={0} flexWrap="wrap" rowGap={3} mb={0} align="flex-end">
          {/* Location */}
          {contact.location && (
            <VStack spacing={1} align="start" py={1} pr={6}>
              <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">Location</Text>
              <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500" pl="2px">{contact.location}</Text>
            </VStack>
          )}

          {/* LinkedIn */}
          {contact.linkedin && (
            <VStack spacing={1} align="start" py={1} pr={6}>
              <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">LinkedIn</Text>
              <Link href={contact.linkedin} isExternal fontSize="xs" fontWeight="500" color="#0077B5" _hover={{ opacity: 0.8 }} onClick={(e) => e.stopPropagation()} pl="2px">
                {contact.linkedin.replace(/^https?:\/\/(www\.)?/, '')}
              </Link>
            </VStack>
          )}

          {/* Email */}
          {contact.email && (
            <VStack spacing={1} align="start" py={1} pr={6}>
              <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">Email</Text>
              <Link href={`mailto:${contact.email}`} fontSize="xs" fontWeight="500" color="blue.400" _hover={{ opacity: 0.8 }} onClick={(e) => e.stopPropagation()} pl="2px">
                {contact.email}
              </Link>
            </VStack>
          )}

          {/* Phone(s) */}
          {phones.length > 0 && phones.map((phone, idx) => (
            <VStack key={idx} spacing={1} align="start" py={1} pr={6}>
              <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">
                {phoneTypes[idx] ? `Phone (${phoneTypes[idx]})` : 'Phone'}
              </Text>
              <Link href={`tel:${phone}`} fontSize="xs" fontWeight="500" color="blue.400" _hover={{ opacity: 0.8 }} onClick={(e) => e.stopPropagation()} pl="2px">
                {formatPhoneNumber(phone)}
              </Link>
            </VStack>
          ))}

        </HStack>

        {/* ───── Divider ───── */}
        <Box h="1px" bg="whiteAlpha.100" my={5} />

        {/* ───── Parent Company section ───── */}
        {(parentCompany || contact.company_name) && (
          <>
            {/* Section header — clickable to collapse */}
            <Flex
              align="center"
              justify="space-between"
              cursor="pointer"
              mb={companyExpanded ? 4 : 0}
              onClick={() => setCompanyExpanded(p => !p)}
              role="group"
            >
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em">
                  Company
                </Text>
              </HStack>
              <Box
                color="whiteAlpha.300"
                opacity={0}
                _groupHover={{ opacity: 1 }}
                transition="opacity 0.15s ease"
              >
                {companyExpanded
                  ? <ChevronUp size={13} strokeWidth={2} />
                  : <ChevronDown size={13} strokeWidth={2} />}
              </Box>
            </Flex>

            {companyExpanded && (
              <Box
                bg="whiteAlpha.30"
                border="1px solid"
                borderColor="whiteAlpha.80"
                borderRadius="10px"
                px={5}
                py={4}
                mb={6}
              >
                {/* Company name + links */}
                <HStack spacing={2} mb={parentCompany ? 4 : 0} flexWrap="wrap">
                  <Flex w="28px" h="28px" borderRadius="7px" bg="whiteAlpha.80" align="center" justify="center" flexShrink={0}>
                    <Building2 size={14} strokeWidth={1.5} color="rgba(255,255,255,0.4)" />
                  </Flex>
                  <Text fontSize="sm" fontWeight="600" color="white">
                    {parentCompany?.company_name || contact.company_name}
                  </Text>
                  {(parentCompany?.linkedin_url) && (
                    <Link href={parentCompany.linkedin_url} isExternal color="#0077B5" _hover={{ color: '#0099d4' }} onClick={e => e.stopPropagation()} display="flex">
                      <Box as={LinkedInIcon} boxSize="14px" />
                    </Link>
                  )}
                  {(parentCompany?.website || contact.company_website) && (
                    <Link
                      href={(() => { const w = parentCompany?.website || contact.company_website; return w?.startsWith('http') ? w : `https://${w}`; })()}
                      isExternal
                      color="blue.400"
                      _hover={{ color: 'blue.300' }}
                      onClick={e => e.stopPropagation()}
                      display="flex"
                    >
                      <ExternalLink size={13} />
                    </Link>
                  )}
                </HStack>

                {/* Company metadata pills */}
                {parentCompany && (
                  <HStack spacing={0} flexWrap="wrap" rowGap={3}>
                    {parentCompany.industry && (
                      <HStack spacing={1.5} pr={5}>
                        <Box color="whiteAlpha.500" flexShrink={0}><Layers size={12} strokeWidth={1.5} /></Box>
                        <Text fontSize="xs" color="white" fontWeight="400">
                          {parentCompany.industry}
                        </Text>
                      </HStack>
                    )}
                    {parentCompany.employees && (
                      <HStack spacing={1.5} pr={5}>
                        <Box color="whiteAlpha.500" flexShrink={0}><Users size={12} strokeWidth={1.5} /></Box>
                        <Text fontSize="xs" color="white" fontWeight="400">
                          {formatEmployees(parentCompany.employees)}
                        </Text>
                      </HStack>
                    )}
                    {parentCompany.revenue_millions && (
                      <HStack spacing={1.5} pr={5}>
                        <Box color="whiteAlpha.500" flexShrink={0}><DollarSign size={12} strokeWidth={1.5} /></Box>
                        <Text fontSize="xs" color="white" fontWeight="400">
                          {formatCurrency(parentCompany.revenue_millions)}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                )}

                {/* Company summary */}
                {parentCompany?.summary && (
                  <Text fontSize="xs" color="white" lineHeight="1.7" mt={3}>
                    {parentCompany.summary}
                  </Text>
                )}
              </Box>
            )}

            <Box h="1px" bg="whiteAlpha.100" mb={5} />
          </>
        )}

        {/* ───── Status & Notes tracking section ───── */}
        <VStack align="stretch" spacing={4} mb={6}>
          <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em">
            Tracking
          </Text>

          {/* Contacted checkbox */}
          <HStack spacing={3}>
            <Checkbox
              isChecked={contactedValue}
              colorScheme="green"
              onChange={(e) => {
                if (contactedFieldId && onContactedChange) {
                  onContactedChange(contact.membership_id, contactedFieldId, e.target.checked);
                }
              }}
            >
              <Text fontSize="sm" color="whiteAlpha.800" fontWeight="500">Contacted</Text>
            </Checkbox>
          </HStack>

          {/* Notes textarea */}
          <VStack align="stretch" spacing={2}>
            <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">Notes</Text>
            <Textarea
              value={notesValue}
              onChange={(e) => {
                const val = e.target.value;
                setNotesValue(val);
                if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
                notesDebounceRef.current = setTimeout(async () => {
                  if (notesFieldId && onNotesChange) {
                    setNotesSaving(true);
                    try {
                      await onNotesChange(contact.membership_id, notesFieldId, val);
                    } finally {
                      setNotesSaving(false);
                    }
                  }
                }, 800);
              }}
              placeholder="Add notes about this contact..."
              bg="#111111"
              border="1px solid"
              borderColor="rgba(255,255,255,0.12)"
              color="#fafafa"
              fontSize="sm"
              _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
              _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
              _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
              borderRadius="8px"
              rows={4}
              resize="vertical"
            />
            {notesSaving && (
              <HStack spacing={1.5}>
                <Spinner size="xs" color="rgba(255,255,255,0.4)" speed="0.8s" />
                <Text fontSize="xs" color="whiteAlpha.400" fontStyle="italic">Saving...</Text>
              </HStack>
            )}
          </VStack>
        </VStack>

        {/* ───── Custom & research fields (chronological) ───── */}
        {detailFields.length > 0 && (
          <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em" mb={2}>
            Custom fields
          </Text>
        )}
        <DetailViewFieldsList
          fields={detailFields}
          dragIdx={dragIdx}
          dragOverIdx={dragOverIdx}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          renamingFieldId={renamingFieldId}
          renameValue={renameValue}
          renameInputRef={renameInputRef}
          onRenameChange={setRenameValue}
          onFinishRename={handleFinishRename}
          onCancelRename={() => { setRenamingFieldId(null); setRenameValue(''); }}
          onFieldContextMenu={(fieldId, x, y) => setContextMenu({ fieldId, x, y })}
          renderFieldValue={renderFieldValue}
          onAddField={openAddFieldModal}
        />

        <Box h="40px" />
      </Box>

      {/* ───── Context menu for dynamic fields ───── */}
      {contextMenu && (
        <Portal>
          <Box
            ref={contextMenuRef}
            position="fixed"
            left={`${contextMenu.x}px`}
            top={`${contextMenu.y}px`}
            bg="#0f0f0f"
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="8px"
            boxShadow="0 8px 24px rgba(0,0,0,0.4)"
            py={1}
            zIndex={2000}
            minW="140px"
          >
            <Flex align="center" px={3} py={2} cursor="pointer"
              _hover={{ bg: 'whiteAlpha.100' }} transition="background 0.1s ease"
              onClick={() => {
                const field = detailFields.find(f => f.id === contextMenu.fieldId);
                if (field) handleStartRename(field);
              }}
            >
              <Box color="whiteAlpha.500" mr={2.5}><Pencil size={13} strokeWidth={1.5} /></Box>
              <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">Rename</Text>
            </Flex>
            <Flex align="center" px={3} py={2} cursor="pointer"
              _hover={{ bg: 'rgba(254,178,178,0.08)' }} transition="background 0.1s ease"
              onClick={() => handleDeleteField(contextMenu.fieldId)}
            >
              <Box color="red.400" mr={2.5}><Trash2 size={13} strokeWidth={1.5} /></Box>
              <Text fontSize="xs" color="red.400" fontWeight="500">Delete</Text>
            </Flex>
          </Box>
        </Portal>
      )}

      {/* ───── Research Field Modal ───── */}
      <Modal isOpen={isOpen} onClose={() => { onClose(); setModalMode('quick'); setCustomPrompt(''); }} isCentered size="md">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="#0a0a0a" border="1px solid" borderColor="rgba(255,255,255,0.10)" borderRadius="12px"
          boxShadow="0 24px 48px rgba(0,0,0,0.6)" mx={4} overflow="hidden">

          <ModalHeader pb={0} pt={5} px={6}>
            <HStack spacing={2.5} mb={2.5}>
              <Flex w="28px" h="28px" borderRadius="7px" bg="rgba(255,255,255,0.08)" align="center" justify="center" flexShrink={0}>
                {modalMode === 'quick'
                  ? <Search size={14} strokeWidth={2} color="rgba(255,255,255,0.7)" />
                  : <Sparkles size={14} strokeWidth={2} color="rgba(255,255,255,0.7)" />}
              </Flex>
              <Text fontSize="md" fontWeight="600" color="#fafafa">
                Research {fullName}
              </Text>
            </HStack>

            <Flex bg="#111111" borderRadius="10px" p="3px" w="100%" border="1px solid" borderColor="rgba(255,255,255,0.08)">
              {[
                { key: 'quick', label: 'Single Field' },
                { key: 'custom', label: 'Deep Dive' },
              ].map((tab) => (
                <Flex key={tab.key} flex={1} align="center" justify="center"
                  py="6px" borderRadius="8px" cursor="pointer"
                  bg={modalMode === tab.key ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  transition="all 0.15s ease"
                  onClick={() => setModalMode(tab.key)}
                >
                  <Text fontSize="xs" fontWeight="600"
                    color={modalMode === tab.key ? '#fafafa' : 'rgba(255,255,255,0.35)'}
                    transition="color 0.15s ease"
                  >
                    {tab.label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,0.4)" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} top={4} right={4} />

          {modalMode === 'quick' && (
            <ModalBody px={6} pb={6} pt={4}>
              <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="400" mb={4} lineHeight="1.5">
                Pick a template or describe your own — we'll search the web and bring back the answer.
              </Text>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1.5}>What do you want to find?</Text>
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>This becomes the label for the data point</Text>
                  <Input
                    placeholder="e.g., Recent Publications"
                    value={fieldName} onChange={(e) => setFieldName(e.target.value)}
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)" color="#fafafa" fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }} _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    borderRadius="8px" h="40px"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1.5}>Describe your research question</Text>
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>Be specific — the more context you give, the better the answer</Text>
                  <Textarea
                    placeholder="e.g., Has this person published any articles or posts recently?"
                    value={fieldDescription} onChange={(e) => setFieldDescription(e.target.value)}
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)" color="#fafafa" fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }} _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    borderRadius="8px" rows={3} resize="none"
                  />
                </Box>
                <Button onClick={handleAddField} isDisabled={!fieldName.trim()}
                  bg="#fafafa" color="#0a0a0a" _hover={{ opacity: 0.85 }}
                  _disabled={{ bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)', cursor: 'not-allowed' }}
                  borderRadius="full" h="40px" fontSize="sm" fontWeight="600" w="100%" mt={1}
                  _focus={{ boxShadow: 'none' }}
                  leftIcon={<Search size={14} strokeWidth={2} />}
                >
                  Research
                </Button>
              </VStack>
            </ModalBody>
          )}

          {modalMode === 'custom' && (
            <ModalBody px={6} pb={6} pt={4}>
              <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="400" mb={4} lineHeight="1.5">
                Describe what you want to research — AI will determine the fields, data types, and structure automatically.
              </Text>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1.5}>What do you want to know?</Text>
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>
                    Be as detailed as you'd like — AI will break this into structured research fields
                  </Text>
                  <Textarea
                    placeholder="e.g., Research this person's recent activity — speaking engagements, publications, and any notable career changes"
                    value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)}
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)" color="#fafafa" fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }} _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    borderRadius="8px" rows={5} resize="none"
                  />
                </Box>
                <Box px={3.5} py={2.5} borderRadius="8px" bg="rgba(255,255,255,0.02)" border="1px solid" borderColor="rgba(255,255,255,0.06)">
                  <HStack spacing={2} align="flex-start">
                    <Sparkles size={12} strokeWidth={2} color="rgba(255,255,255,0.35)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Text fontSize="xs" color="rgba(255,255,255,0.4)" lineHeight="1.6">
                      AI will analyze your prompt, determine how many fields to create, choose the right data types, and generate a research-optimized query.
                    </Text>
                  </HStack>
                </Box>
                <Button
                  onClick={handleDeepDive}
                  isLoading={deepDiveLoading}
                  loadingText="Analyzing..."
                  isDisabled={!customPrompt.trim() || deepDiveLoading}
                  bg="#fafafa" color="#0a0a0a" _hover={{ opacity: 0.85 }}
                  _disabled={{ bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)', cursor: 'not-allowed' }}
                  borderRadius="full" h="40px" fontSize="sm" fontWeight="600" w="100%" mt={1}
                  _focus={{ boxShadow: 'none' }}
                  leftIcon={!deepDiveLoading ? <Sparkles size={14} strokeWidth={2} /> : undefined}
                >
                  Generate Research Fields
                </Button>
              </VStack>
            </ModalBody>
          )}
        </ModalContent>
      </Modal>

      {/* ───── Delete Confirmation Modal ───── */}
      <Modal isOpen={!!deleteConfirm} onClose={() => !isDeleting && setDeleteConfirm(null)} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="#0a0a0a" border="1px solid" borderColor="rgba(255,255,255,0.10)" borderRadius="12px"
          boxShadow="0 24px 48px rgba(0,0,0,0.6)" mx={4} overflow="hidden">
          <ModalHeader pb={0} pt={5} px={6}>
            <HStack spacing={2.5}>
              <Flex w="28px" h="28px" borderRadius="7px" bg="rgba(239,68,68,0.15)" align="center" justify="center" flexShrink={0}>
                <AlertTriangle size={14} strokeWidth={2} color="rgba(239,68,68,0.85)" />
              </Flex>
              <Text fontSize="md" fontWeight="600" color="#fafafa">Delete field</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,0.4)" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} top={4} right={4} />
          <ModalBody px={6} pb={6} pt={4}>
            <Text fontSize="sm" color="rgba(255,255,255,0.5)" lineHeight="1.6" mb={5}>
              <Text as="span" color="#fafafa" fontWeight="600">"{deleteConfirm?.label}"</Text>
              {' '}has data across{' '}
              <Text as="span" color="#fafafa" fontWeight="600">{deleteConfirm?.valueCount} contacts</Text>.
              This will permanently remove the field and all its values.
            </Text>
            <HStack spacing={3} w="100%">
              <Button flex={1} size="sm" variant="ghost" color="rgba(255,255,255,0.5)"
                _hover={{ bg: 'rgba(255,255,255,0.06)', color: '#fafafa' }}
                _focus={{ boxShadow: 'none' }}
                borderRadius="full" h="36px" fontWeight="500"
                onClick={() => setDeleteConfirm(null)} isDisabled={isDeleting}
              >
                Cancel
              </Button>
              <Button flex={1} size="sm"
                bg="rgba(239,68,68,0.15)" color="rgba(239,68,68,0.85)"
                border="1px solid rgba(239,68,68,0.25)"
                _hover={{ bg: 'rgba(239,68,68,0.25)' }}
                _focus={{ boxShadow: 'none' }}
                borderRadius="full" h="36px" fontWeight="600"
                leftIcon={!isDeleting ? <Trash2 size={13} strokeWidth={2} /> : undefined}
                isLoading={isDeleting} loadingText="Deleting..."
                onClick={() => executeDelete(deleteConfirm.fieldId)}
              >
                Delete for all
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      </Box>{/* end inner scrollable Box */}

      {/* Panel-edge navigation arrows */}
      {(onPrev || onNext) && (
        <>
          <Flex
            as="button"
            onClick={onPrev}
            disabled={!onPrev}
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            zIndex={10}
            w="36px" h="56px"
            align="center"
            justify="center"
            bg="transparent"
            color={onPrev ? 'rgba(255,255,255,0.4)' : 'whiteAlpha.200'}
            cursor={onPrev ? 'pointer' : 'not-allowed'}
            _hover={onPrev ? { color: '#fafafa' } : {}}
            _active={{}}
          >
            <ChevronLeft size={18} strokeWidth={2} />
          </Flex>
          <Flex
            as="button"
            onClick={onNext}
            disabled={!onNext}
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            zIndex={10}
            w="36px" h="56px"
            align="center"
            justify="center"
            bg="transparent"
            color={onNext ? 'rgba(255,255,255,0.4)' : 'whiteAlpha.200'}
            cursor={onNext ? 'pointer' : 'not-allowed'}
            _hover={onNext ? { color: '#fafafa' } : {}}
            _active={{}}
          >
            <ChevronRight size={18} strokeWidth={2} />
          </Flex>
        </>
      )}
    </Box>
  );
};

export default ContactDetailView;
